// Menú de texto de arriba, como en Random: muestra SOLO los menús del subsistema elegido en la barra
// de íconos (Ventas → Negocios · Punto de Venta · Documentos · Clientes y Precios · Consultas). Cada
// menú se despliega en grupos con su detalle. Un clic lo abre; con uno abierto, pasar el mouse por
// otro lo cambia; Esc o un clic afuera lo cierra. Abrir un menú NO crea pestañas. La estrella de cada
// función la fija en los accesos rápidos.
import { useEffect, useRef, useState, type RefObject } from 'react';
import { Icono } from '../comun/Icono';
import { InsigniaBackend } from '../comun/ui';
import { menusDe, type Modulo, type Pantalla } from './navegacion';

export function MenuPrincipal({
  subsistema,
  nombresCortos,
  favoritos,
  alElegir,
  alAlternarFavorito,
}: {
  /** El subsistema elegido (ya filtrado por permisos); null si el usuario no ve ninguno. */
  subsistema: Modulo | null;
  nombresCortos: boolean;
  favoritos: string[];
  alElegir: (p: Pantalla) => void;
  alAlternarFavorito: (ruta: string) => void;
}) {
  const caja = useRef<HTMLDivElement>(null);
  const [abierto, setAbierto] = useMenuAbierto(caja);
  const menus = subsistema ? menusDe(subsistema) : [];

  return (
    <div className="menu-principal" role="menubar" aria-label="Menú principal" ref={caja}>
      {subsistema && (
        <span className="nombre-subsistema" data-subsistema={subsistema.id} title="Subsistema">
          {nombresCortos ? subsistema.corto : subsistema.nombre}
        </span>
      )}
      {menus.map((menu) => {
        const esAbierto = abierto === menu;
        return (
          <div key={menu} className="entrada-menu">
            <button
              type="button"
              role="menuitem"
              className={`boton-modulo${esAbierto ? ' abierto' : ''}`}
              data-menu={menu}
              title={menu}
              aria-haspopup="menu"
              aria-expanded={esAbierto}
              onClick={() => setAbierto(esAbierto ? null : menu)}
              onMouseEnter={() => abierto && abierto !== menu && setAbierto(menu)}
            >
              {menu}
            </button>
            {esAbierto && subsistema && (
              <DesplegableModulo
                modulo={{
                  ...subsistema,
                  nombre: menu,
                  secciones: subsistema.secciones.filter((s) => s.menu === menu),
                }}
                favoritos={favoritos}
                alElegir={(p) => {
                  setAbierto(null);
                  alElegir(p);
                }}
                alAlternarFavorito={alAlternarFavorito}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Un solo menú abierto a la vez; Esc o un clic fuera de `caja` lo cierra. */
export function useMenuAbierto(caja: RefObject<HTMLElement | null>) {
  const [abierto, setAbierto] = useState<string | null>(null);
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (!caja.current?.contains(e.target as Node)) setAbierto(null);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(null);
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', esc);
    };
  }, [abierto, caja]);
  return [abierto, setAbierto] as const;
}

/** Las funciones de un módulo (el mismo desplegable desde el menú de texto o desde su ícono). */
export function DesplegableModulo({
  modulo: m,
  favoritos,
  alElegir,
  alAlternarFavorito,
}: {
  modulo: Modulo;
  favoritos: string[];
  alElegir: (p: Pantalla) => void;
  alAlternarFavorito: (ruta: string) => void;
}) {
  const total = m.secciones.reduce((n, s) => n + s.pantallas.length, 0);
  // Si alguna función del menú tiene sigla (NVV, GDV…), todas reservan su columna para alinear.
  const conCodigos = m.secciones.some((s) => s.pantallas.some((p) => p.codigo));
  return (
    <div
      className={`desplegable-modulo${total > 14 ? ' en-columnas' : ''}`}
      role="menu"
      aria-label={m.nombre}
    >
      {m.secciones.map((s, i) => (
        <div key={s.nombre ?? i} className="grupo-menu">
          {s.nombre && (
            <div className="titulo-grupo-menu">
              <span className="flecha" aria-hidden>
                ▸
              </span>
              {s.nombre}
            </div>
          )}
          {s.pantallas
            .filter((p) => !p.oculta)
            .map((p) => {
              const fijada = favoritos.includes(p.ruta);
              return (
                <div key={p.ruta} className="fila-menu">
                  <button
                    type="button"
                    role="menuitem"
                    className={`${p.accion ? 'operacion' : ''}${p.abre ? '' : ' preparada'}`}
                    data-ruta={p.ruta}
                    title={p.descripcion}
                    onClick={() => alElegir(p)}
                  >
                    {conCodigos && (
                      <span className="codigo" aria-hidden>
                        {p.codigo}
                      </span>
                    )}
                    <span className="nombre">{p.nombre}</span>
                    {p.tecla && <kbd aria-hidden>{p.tecla}</kbd>}
                    {p.backend !== 'A' && <InsigniaBackend estado={p.backend} />}
                  </button>
                  <button
                    type="button"
                    className={`fijar${fijada ? ' fijada' : ''}`}
                    aria-pressed={fijada}
                    aria-label={
                      fijada
                        ? `Quitar ${p.nombre} de accesos rápidos`
                        : `Fijar ${p.nombre} en accesos rápidos`
                    }
                    title={fijada ? 'Quitar de accesos rápidos' : 'Fijar en accesos rápidos'}
                    onClick={() => alAlternarFavorito(p.ruta)}
                  >
                    <Icono nombre="estrella" tamano={13} relleno={fijada} />
                  </button>
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
}
